"use client";

import { useCallback, useMemo } from "react";
import { useLocale } from "./useLocale";
import { translate } from "@/lib/i18n/messages";

/**
 * Returns a stable `t(key, vars?)` function bound to the current
 * locale. Re-renders the consuming component when the user toggles
 * language via the header `LanguageSelector` (locale change event).
 *
 *   const t = useT();
 *   <span>{t("footer.live")}</span>
 *   <span>{t("tooltip.tradersCount", { count: 1_234 })}</span>
 *
 * `{name}` placeholders inside the catalog string are replaced with
 * the matching key in `vars`. Numbers are formatted with
 * `toLocaleString` in the active locale so thousands separators flip
 * EN ("1,234") ↔ ES ("1.234") for free.
 */
export function useT() {
  const locale = useLocale();
  return useCallback(
    (key: string, vars?: Record<string, string | number>): string => {
      const raw = translate(locale, key);
      if (!vars) return raw;
      return raw.replace(/\{(\w+)\}/g, (_, k) => {
        const v = vars[k];
        if (v == null) return `{${k}}`;
        return typeof v === "number" ? v.toLocaleString(locale) : String(v);
      });
    },
    [locale],
  );
}

/**
 * Translate a Polymarket category/tag slug to its display label.
 * gamma-api ships the slug only — we own the text. Falls back to a
 * Title-Case render of the slug ("nba-east" → "Nba East") so newly
 * surfaced categories don't show as raw kebab-case while we add them
 * to the catalog.
 */
export function useTCategory() {
  const locale = useLocale();
  return useMemo(() => {
    return (slug: string | null | undefined): string => {
      if (!slug) return "";
      const key = `category.${slug.toLowerCase()}`;
      const translated = translate(locale, key);
      if (translated !== key) return translated;
      // Fallback: Title Case the slug so unknown tags still read OK.
      return slug
        .split("-")
        .map((p) => (p.length > 0 ? p[0].toUpperCase() + p.slice(1) : p))
        .join(" ");
    };
  }, [locale]);
}

/**
 * Translate a Kalshi subcategory `name` (e.g. "Bitcoin", "Football",
 * "AI"). Backend returns these in English — we look up
 * `kalshiSubcat.{slug}` (slug = name lowercased, spaces → "-") and
 * fall back to the original name when no translation exists.
 */
export function useTKalshiSubcat() {
  const locale = useLocale();
  return useMemo(() => {
    return (name: string | null | undefined): string => {
      if (!name) return "";
      const slug = name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
      const key = `kalshiSubcat.${slug}`;
      const translated = translate(locale, key);
      if (translated !== key) return translated;
      return name;
    };
  }, [locale]);
}
